import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { ReferAFriendStatsHistory } from '../referAFriendStatsHistory'
import { Box, InfoBox } from '../../atoms/box'
import { Button } from '../../atoms/button'
import { Flex } from '../../atoms/flex'
import { If } from '../../atoms/if'
import { Image } from '../../atoms/image'
import { Statistic } from '../../atoms/statistic'
import { Tag } from '../../atoms/tag'
import { Title, Text } from '../../atoms/text'
import EligibleIcon from '../../../images/refer-a-friend/icon_referfriend_status_eligible.svg'
import ExpiredIcon from '../../../images/refer-a-friend/icon_referfriend_status_expired.svg'
import ReferredIcon from '../../../images/refer-a-friend/icon_referfriend_status_referred.svg'
import GreyTimerIcon from '../../../images/refer-a-friend/icon_timer_grey.svg'
import TimerIcon from '../../../images/refer-a-friend/icon_timer.svg'
import useDeviceDetect from '../../../hooks/useDeviceDetect'
import { themeStyles } from '../../../styles/theme'
import { useTranslation } from '../../../context/translationProvider'
import { getCurrentStats } from '../../../adapters/referAFriend'
import { intervalToDuration } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { monthsNames } from '../../../utils/constants'
import { Steps } from '../steps'

const today = new Date()
const month = today?.getMonth()

const MET_REQUIREMENTS = 'MET_REQUIREMENTS'
const REQUIRES_DEPOSIT = 'REQUIRES_DEPOSIT'
const REQUIRES_BET = 'REQUIRES_BET'
const NOT_ELIGIBLE = 'NOT_ELIGIBLE'

export const ReferAFriendAccountStatsTab = ({
  toggleTabs,
  remaining,
  showHistory,
  setShowHistory,
}) => {
  const { translate } = useTranslation()
  const { isMobile } = useDeviceDetect()
  const [successfulReferrals, setSuccessfulReferrals] = useState([])
  const [eligibleReferrals, setEligibleReferrals] = useState([])
  const [expiredReferrals, setExpiredReferrals] = useState([])
  const currentMonth = translate(`common.${monthsNames[month]}`)

  const toggleHistory = () => {
    setShowHistory((current) => !current)
    toggleTabs()
  }

  const getData = async () => {
    const { data } = await getCurrentStats()
    setSuccessfulReferrals(
      data?.filter((item) => item?.status === MET_REQUIREMENTS)
    )
    setEligibleReferrals(
      data?.filter(
        (item) =>
          item?.status === REQUIRES_DEPOSIT || item?.status === REQUIRES_BET
      )
    )
    setExpiredReferrals(data?.filter((item) => item?.status === NOT_ELIGIBLE))
  }

  useEffect(() => {
    getData()
  }, [])

  const getEligibleStep = (status) => {
    switch (status) {
      case REQUIRES_DEPOSIT:
        return 0

      default:
      case REQUIRES_BET:
        return 1
    }
  }

  const getLeftDays = (expiresAt) => {
    const { days, hours } = intervalToDuration({
      start: new Date(),
      end: utcToZonedTime(expiresAt, 'UTC'),
    })

    const daysLeft = days < 1 && hours > 0 ? 1 : days

    return `${daysLeft} ${
      daysLeft === 1
        ? translate('referAFriend.dayLeft')
        : translate('referAFriend.daysLeft')
    }`
  }

  if (showHistory) {
    return <ReferAFriendStatsHistory toggleHistory={toggleHistory} />
  }

  return (
    <Box padding={isMobile ? '0.5em 0 5em 0' : '2em 0 5em'}>
      <Flex direction="column" gap="2.5em">
        <Flex direction="column">
          <Title level={2} style={{ marginBottom: isMobile ? ' 0.25em' : 0 }}>
            {currentMonth}
          </Title>
          <Flex justifyContent="space-between" alignItems="flex-end">
            <Flex gap="5px">
              <Text noMargin>{remaining}</Text>
              <Text noMargin>
                {translate('referAFriend.remainingReferrals')}
              </Text>
            </Flex>
            <If
              condition={!isMobile}
              render={() => (
                <Button
                  size="extraSmall"
                  minWidth="0px"
                  rounded
                  uppercase={false}
                  bold={false}
                  black
                  onClick={toggleHistory}
                >
                  <Flex alignItems="center" gap="0.25em">
                    <Image url={TimerIcon} width={24} />
                    <Text
                      noMargin
                      style={{
                        letterSpacing: 0,
                        color: themeStyles.colors.white.main,
                      }}
                    >
                      {translate('common.history')}
                    </Text>
                  </Flex>
                </Button>
              )}
            />
          </Flex>
        </Flex>

        <Flex justifyContent="space-between" gap="1em">
          <Statistic
            value={successfulReferrals?.length}
            label={translate('referAFriend.successfulReferrals.title')}
            theme="success"
          />
          <Statistic
            value={eligibleReferrals?.length}
            label={translate('referAFriend.eligibleReferrals.title')}
            theme="info"
          />
          <Statistic
            value={expiredReferrals?.length}
            label={translate('referAFriend.expiredReferrals.title')}
          />
        </Flex>

        <If
          condition={isMobile}
          render={() => (
            <Flex justifyContent="center">
              <Button
                size="extraSmall"
                mobileExpand={false}
                minWidth="0px"
                rounded
                uppercase={false}
                bold={false}
                black
                onClick={toggleHistory}
              >
                <Flex alignItems="center" gap="0.25em">
                  <Image url={TimerIcon} width={24} />
                  <Text noMargin style={{ letterSpacing: 0 }}>
                    {translate('common.history')}
                  </Text>
                </Flex>
              </Button>
            </Flex>
          )}
        />

        <Flex direction="column" gap="0.5em">
          <Flex gap="0.5em">
            <Image url={ReferredIcon} width={24} />
            <Title level={3}>
              {translate('referAFriend.successfulReferrals.title')}
            </Title>
          </Flex>
          <Text>
            {translate('referAFriend.successfulReferrals.description')}
          </Text>
          <If
            condition={successfulReferrals?.length === 0}
            render={() => (
              <InfoBox>
                <Text noMargin small={isMobile}>
                  {translate('referAFriend.noSuccessfulReferrals')}
                </Text>
              </InfoBox>
            )}
            renderElse={() =>
              successfulReferrals?.map((item) => (
                <InfoBox theme="success" key={item?.initials}>
                  <Flex
                    justifyContent="space-between"
                    alingItems="center"
                    ga="1em"
                  >
                    <Text
                      small={isMobile}
                      noMargin
                      truncate
                      truncateWidth={isMobile ? '50px' : null}
                    >
                      {item?.initials}
                    </Text>
                    <Flex alingItems="center">
                      <Tag
                        theme="green"
                        style={{ fontSize: 13 }}
                        fontWeight="700"
                        text={translate('referAFriend.referred')}
                      />
                    </Flex>
                  </Flex>
                </InfoBox>
              ))
            }
          />
        </Flex>

        <Flex direction="column" gap="0.5em">
          <Flex gap="0.5em">
            <Image url={EligibleIcon} width={24} />
            <Title level={3}>
              {translate('referAFriend.eligibleReferrals.title')}
            </Title>
          </Flex>
          <Text>{translate('referAFriend.eligibleReferrals.description')}</Text>

          <If
            condition={eligibleReferrals?.length === 0}
            render={() => (
              <InfoBox>
                <Text noMargin small={isMobile}>
                  {translate('referAFriend.noEligibleReferrals')}
                </Text>
              </InfoBox>
            )}
            renderElse={() =>
              eligibleReferrals?.map((item) => (
                <InfoBox theme="lightInfo" key={item?.initials}>
                  <Flex direction="column" gap="1em">
                    <Flex
                      justifyContent="space-between"
                      alingItems="center"
                      ga="1em"
                    >
                      <Text
                        small={isMobile}
                        noMargin
                        truncate
                        truncateWidth={isMobile ? '50px' : null}
                      >
                        {item?.initials}
                      </Text>
                      <Flex alingItems="center" gap="0.25em">
                        <Flex>
                          <Image
                            url={GreyTimerIcon}
                            width={24}
                            style={{ alignSelf: 'center' }}
                          />
                        </Flex>
                        <Text theme="light" small={isMobile} noMargin>
                          {getLeftDays(item?.expiresAt)}
                        </Text>
                      </Flex>
                    </Flex>
                    <Steps
                      steps={[
                        translate('referAFriend.registerStep'),
                        translate('referAFriend.depositStep'),
                        translate('referAFriend.betStep'),
                      ]}
                      theme="check"
                      backgroundContent={
                        themeStyles.colors.info.lightInfo2.main
                      }
                      currentStep={getEligibleStep(item?.status)}
                    />
                    <If
                      condition={remaining === 0}
                      render={() => (
                        <Text theme="light" small={isMobile} noMargin>
                          {translate('referAFriend.exceededEligibleWarning')}
                        </Text>
                      )}
                    />
                  </Flex>
                </InfoBox>
              ))
            }
          />
        </Flex>

        <Flex direction="column" gap="0.5em">
          <Flex gap="0.5em">
            <Image url={ExpiredIcon} width={24} />
            <Title level={3}>
              {translate('referAFriend.expiredReferrals.title')}
            </Title>
          </Flex>
          <Text>{translate('referAFriend.expiredReferrals.description')}</Text>

          <If
            condition={expiredReferrals?.length === 0}
            render={() => (
              <InfoBox>
                <Text noMargin small={isMobile}>
                  {translate('referAFriend.noExpiredReferrals')}
                </Text>
              </InfoBox>
            )}
            renderElse={() =>
              expiredReferrals?.map((item) => (
                <InfoBox key={item?.initials}>
                  <Flex direction="column" gap="0.5em">
                    <Flex
                      justifyContent="space-between"
                      alingItems="center"
                      ga="1em"
                    >
                      <Text
                        small={isMobile}
                        noMargin
                        truncate
                        truncateWidth={isMobile ? '50px' : null}
                      >
                        {item?.initials}
                      </Text>
                      <Flex alingItems="center">
                        <Tag
                          theme="darkGrey5"
                          size="large"
                          fontWeight="600"
                          text={translate('referAFriend.expired')}
                        />
                      </Flex>
                    </Flex>
                    <Text theme="light" small={isMobile} noMargin>
                      {translate('referAFriend.notMeetRequirementsWarning')}
                    </Text>
                  </Flex>
                </InfoBox>
              ))
            }
          />
        </Flex>
      </Flex>
    </Box>
  )
}

ReferAFriendAccountStatsTab.defaultProps = {
  toggleTabs: () => {},
  remaining: 0,
}

ReferAFriendAccountStatsTab.propTypes = {
  toggleTabs: PropTypes.func,
  remaining: PropTypes.number,
}
